import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getRegistrationsGroupedByDistance } from '../api';

const DistanceRegistrationsPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCsv, setShowCsv] = useState(false);
    const { logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getRegistrationsGroupedByDistance();
                setData(processDataForHeats(response.data));
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [logout, navigate]);

    // Новая логика распределения по заплывам
    const processDataForHeats = (data) => {
        return data.map(distanceGroup => {
            // Сортируем участников по времени (от слабых к сильным)
            const sortedSwimmers = [...distanceGroup.swimmers]
                .sort((a, b) => (b.entry_time_seconds || 0) - (a.entry_time_seconds || 0));

            const totalSwimmers = sortedSwimmers.length;
            const swimmersPerHeat = 6;

            const numberOfHeats = Math.ceil(totalSwimmers / swimmersPerHeat);

            if (totalSwimmers === 0) {
                return {
                    ...distanceGroup,
                    heats: []
                };
            }

            const swimmersPerHeatArray = [];
            let remainingSwimmers = totalSwimmers;

            for (let i = 0; i < numberOfHeats; i++) {
                const swimmersForThisHeat = Math.ceil(remainingSwimmers / (numberOfHeats - i));
                swimmersPerHeatArray.push(swimmersForThisHeat);
                remainingSwimmers -= swimmersForThisHeat;
            }

            const heats = [];
            let startIndex = 0;

            for (let i = 0; i < numberOfHeats; i++) {
                const count = swimmersPerHeatArray[i];
                const swimmersForHeat = sortedSwimmers.slice(startIndex, startIndex + count);
                startIndex += count;

                const sortedForHeat = [...swimmersForHeat].sort(
                    (a, b) => (a.entry_time_seconds || 0) - (b.entry_time_seconds || 0)
                );

                const heat = distributeToLanes(sortedForHeat);
                heats.push(heat);
            }

            return {
                ...distanceGroup,
                heats: heats.map((heat, index) => ({
                    heatNumber: index + 1,
                    lanes: heat
                }))
            };
        });
    };

    const distributeToLanes = (swimmers) => {
        const lanes = Array(8).fill(null);
        const lanePriority = [4, 5, 3, 6, 2, 7];

        swimmers.forEach((swimmer, index) => {
            if (index < lanePriority.length) {
                lanes[lanePriority[index] - 1] = swimmer;
            }
        });

        return lanes;
    };

    // Генерация CSV данных
// Генерация CSV данных с разметкой по дистанциям и заплывам
    const generateCsvData = () => {
        let csvRows = [];

        // Заголовок CSV
        csvRows.push('Сводка по заплывам');
        csvRows.push('');
        csvRows.push('Дата генерации: ' + new Date().toLocaleString());
        csvRows.push('');

        data.forEach((group, groupIndex) => {
            // Разделитель дистанции
            if (groupIndex > 0) {
                csvRows.push('');
                csvRows.push(''); // Пустая строка для разделения
            }

            // Заголовок дистанции
            const genderText = group.distance.gender_restriction === 'Male' ? 'Мужчины' : 'Женщины';
            csvRows.push(`"=== ${group.distance.name} ${group.distance.gender_restriction ? `(${genderText})` : ''} ==="`);
            csvRows.push('');

            if (group.heats.length === 0) {
                csvRows.push('Нет зарегистрированных участников');
                csvRows.push('');
                return;
            }

            group.heats.forEach((heat, heatIndex) => {
                // Заголовок заплыва
                csvRows.push(`Заплыв ${heat.heatNumber}`);

                // Заголовки колонок для заплыва
                csvRows.push('Дорожка,Фамилия,Имя,Город,Клуб,Возрастная группа,Заявочное время,Время в секундах');

                // Данные заплыва
                heat.lanes.forEach((swimmer, laneIndex) => {
                    const laneNumber = laneIndex + 1;
                    if (swimmer) {
                        const row = [
                            laneNumber,
                            `"${swimmer.last_name || ''}"`,
                            `"${swimmer.first_name || ''}"`,
                            `"${swimmer.city || ''}"`,
                            `"${swimmer.club === 'Лично (без клуба)' ? 'Лично' : (swimmer.club || '')}"`,
                            `"${swimmer.age_group || ''}"`,
                            `"${swimmer.entry_time || ''}"`,
                            swimmer.entry_time_seconds || ''
                        ];
                        csvRows.push(row.join(','));
                    } else {
                        // Пустые дорожки (опционально)
                        const row = [
                            laneNumber,
                            '', '', '', '', '', '', ''
                        ];
                        csvRows.push(row.join(','));
                    }
                });

                // Пустая строка после заплыва
                csvRows.push('');
            });
        });

        return csvRows.join('\n');
    };

    // Копирование CSV в буфер обмена
// Копирование CSV в буфер обмена с форматированием
    const copyCsvToClipboard = () => {
        const csvData = generateCsvData();

        // Для лучшего форматирования в Excel
        const formattedData = csvData;

        navigator.clipboard.writeText(formattedData).then(() => {
            toast.success('CSV скопирован в буфер обмена!');
        }).catch(() => {
            // Fallback
            const textarea = document.createElement('textarea');
            textarea.value = formattedData;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            toast.success('CSV скопирован в буфер обмена!');
        });
    };

    // Скачивание CSV файла
    const downloadCsv = () => {
        const csvData = generateCsvData();
        const blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' }); // \uFEFF для UTF-8 BOM
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'registrations_heats.csv';
        link.click();
        toast.success('CSV файл скачан!');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold">Регистрации по дистанциям</h1>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setShowCsv(!showCsv)}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                        {showCsv ? 'Скрыть CSV' : 'Показать CSV'}
                    </button>
                    <button
                        onClick={copyCsvToClipboard}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Копировать CSV
                    </button>
                    <button
                        onClick={downloadCsv}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                        Скачать CSV
                    </button>
                </div>
            </div>

            {/* Блок с CSV */}
            {showCsv && (
                <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-300">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold">CSV данные</h2>
                        <span className="text-sm text-gray-500">
                            Нажмите "Копировать CSV" чтобы скопировать в буфер обмена
                        </span>
                    </div>
                    <textarea
                        className="w-full h-64 p-2 border border-gray-300 rounded font-mono text-sm bg-white"
                        readOnly
                        value={generateCsvData()}
                    />
                    <div className="mt-2 text-sm text-gray-500">
                        <p>Разделитель: запятая (,) | Кодировка: UTF-8</p>
                        <p>Для вставки в Excel используйте: Данные → Из текстового/CSV файла</p>
                    </div>
                </div>
            )}

            {data.map((group, groupIndex) => (
                <div key={group.distance.id} className="mb-12">
                    <h2 className="text-xl font-semibold mb-4">
                        {group.distance.name}
                        {group.distance.gender_restriction && (
                            <span className="ml-2 text-sm text-gray-500">
                                ({group.distance.gender_restriction === 'Male' ? 'Мужчины' : 'Женщины'})
                            </span>
                        )}
                    </h2>

                    {group.heats.length > 0 ? (
                        <div className="space-y-8">
                            {group.heats.map((heat, heatIndex) => (
                                <div key={heatIndex} className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="text-lg font-medium mb-4">Заплыв {heat.heatNumber}</h3>
                                    <table className="min-w-full bg-white border border-gray-200" style={{width:1200+"px", textAlign: "center"}}>
                                        <thead>
                                        <tr>
                                            <th style={{width:70+"px"}}>№ дор</th>
                                            <th style={{width:200+"px"}}>Фамилия</th>
                                            <th style={{width:180+"px"}}>Имя</th>
                                            <th style={{width:210+"px"}}>Город</th>
                                            <th style={{width:210+"px"}}>Клуб</th>
                                            <th style={{width:70+"px"}}>Возр гр</th>
                                            <th style={{width:150+"px"}}>Заявочное время</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {heat.lanes.map((swimmer, laneIndex) => (
                                            <tr key={laneIndex}>
                                                <td className="text-center font-medium">
                                                    {laneIndex + 1}
                                                </td>
                                                {swimmer ? (
                                                    <>
                                                        <td className="text-center">{swimmer.last_name}</td>
                                                        <td className="text-center">{swimmer.first_name}</td>
                                                        <td>{swimmer.city || '-'}</td>
                                                        <td className="text-center">
                                                            {swimmer.club === "Лично (без клуба)" ? "Лично" : swimmer.club || '-'}
                                                        </td>
                                                        <td>{swimmer.age_group}+</td>
                                                        <td className="text-center font-mono">
                                                            {swimmer.entry_time || 'Без времени'}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="text-center text-gray-400">—</td>
                                                        <td className="text-center text-gray-400">—</td>
                                                        <td className="text-center text-gray-400">—</td>
                                                        <td className="text-center text-gray-400">—</td>
                                                        <td className="text-center text-gray-400">—</td>
                                                        <td className="text-center text-gray-400">—</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">Нет зарегистрированных участников</p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default DistanceRegistrationsPage;
/*import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getRegistrationsGroupedByDistance } from '../api';

const DistanceRegistrationsPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const { logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getRegistrationsGroupedByDistance();
                setData(response.data);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [logout, navigate]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-8">Регистрации по дистанциям</h1>

            {data.map((group, index) => (
                <div key={group.distance.id} className="mb-12">
                    <h2 className="text-xl font-semibold mb-4">
                        {group.distance.name}
                        {group.distance.gender_restriction && (
                            <span className="ml-2 text-sm text-gray-500">
                                ({group.distance.gender_restriction === 'Male' ? 'Мужчины' : 'Женщины'})
                            </span>
                        )}
                    </h2>

                    {group.swimmers.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Участник</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Клуб</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Город</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Возрастная группа</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заявочное время</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус оплаты</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {group.swimmers.map(swimmer => (
                                    <tr key={swimmer.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">{swimmer.full_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{swimmer.club}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{swimmer.city}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{swimmer.age_group}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{swimmer.entry_time || 'Не указано'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                                                    ${swimmer.payment_status === 'Оплачено' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {swimmer.payment_status}
                                                </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-gray-500">Нет зарегистрированных участников</p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default DistanceRegistrationsPage;
*/
